import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Modal, Form, Button, Spinner } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faSearch, faPlus, faEdit, faHandHoldingDollar,
  faCheckCircle, faExclamationTriangle,
  faUser, faSyncAlt, faFileCsv, faTrash, faSpinner
} from '@fortawesome/free-solid-svg-icons';
import Dexie from 'dexie';
import API_CONFIG2 from '../../API_CONFIG/API_CONFIG2';
import { Link } from 'react-router-dom';

// ============================================================
// 1. UTILITAIRE FETCH JSON SÉCURISÉ
// ============================================================
const fetchJSON = async (url, options = {}) => {
  const response = await fetch(url, options);
  const text = await response.text();
  if (!response.ok) {
    throw new Error(`HTTP ${response.status} : ${text || 'Erreur inconnue'}`);
  }
  try {
    return JSON.parse(text);
  } catch (parseError) {
    throw new Error(`Réponse JSON invalide : ${text.substring(0, 200) || '(réponse vide)'}`);
  }
};

// ============================================================
// 2. CACHE DEXIE (24h) avec fallback périmé
// ============================================================
const db = new Dexie('EmployesCacheDB');
db.version(1).stores({
  employes: 'key, data, timestamp'
});
const CACHE_DURATION = 24 * 60 * 60 * 1000;

async function getCachedEmployes(key, allowExpired = false) {
  try {
    const entry = await db.employes.get(key);
    if (!entry) return null;
    const age = Date.now() - entry.timestamp;
    if (!allowExpired && age > CACHE_DURATION) return null;
    return entry.data;
  } catch {
    return null;
  }
}

async function setCachedEmployes(key, data) {
  await db.employes.put({ key, data, timestamp: Date.now() });
}

async function getStaleEmployes(key) {
  return getCachedEmployes(key, true);
}

// ============================================================
// 3. DÉDOUBLONNAGE DES REQUÊTES EN COURS (Map globale)
// ============================================================
const pendingFetches = new Map();

const API_URL = `${API_CONFIG2.apiUrl}paie/api_employe2.php`;

const ManageEmployesModal = ({ userId }) => {
  // --- États ---
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [pagination, setPagination] = useState({ currentPage: 1, totalPages: 1, totalRecords: 0, limit: 10 });
  const [dataSource, setDataSource] = useState('cache');

  // Modale de création/édition de fiche de paie
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [editingPayroll, setEditingPayroll] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    id: '',
    userId: userId,
    employeId: '',
    periode: '',
    nom: '',
    prenom: '',
    salaireBase: 0,
    primes: 0,
    cotisations: 0,
    netAPayer: 0,
    statut: 'Payé'
  });

  // Modale de suppression
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteId, setDeleteId] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const [toast, setToast] = useState({ show: false, message: '', variant: 'success' });
  const searchTimeoutRef = useRef(null);
  const abortControllerRef = useRef(null);
  const toastTimeoutRef = useRef(null);
  const isMounted = useRef(true);

  // ============================================================
  // 4. GESTION DES TOASTS
  // ============================================================
  const showToast = useCallback((message, variant = 'success') => {
    if (toastTimeoutRef.current) clearTimeout(toastTimeoutRef.current);
    setToast({ show: true, message, variant });
    toastTimeoutRef.current = setTimeout(() => {
      if (isMounted.current) setToast({ show: false });
      toastTimeoutRef.current = null;
    }, 5000);
  }, []);

  // ============================================================
  // 5. RÉCUPÉRATION DES EMPLOYÉS (avec cache, dédoublonnage, annulation)
  // ============================================================
  const fetchEmployees = useCallback(async (page = 1, search = '', forceRefresh = false, retryCount = 0) => {
    // Annuler la requête précédente
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    const controller = new AbortController();
    abortControllerRef.current = controller;

    const cacheKey = `employes_page_${page}_search_${search}`;
    const requestKey = forceRefresh ? `${cacheKey}_force` : cacheKey;

    // Dédoublonnage : si une requête identique est déjà en cours, on attend sa promesse
    if (pendingFetches.has(requestKey) && !forceRefresh) {
      try {
        const result = await pendingFetches.get(requestKey);
        if (isMounted.current && result) {
          setEmployees(result.data);
          setPagination(result.pagination);
          setDataSource('cache (partagé)');
          setLoading(false);
          setRefreshing(false);
        }
        return;
      } catch {
        pendingFetches.delete(requestKey);
      }
    }

    // Limiter les tentatives pour éviter les boucles infinies
    if (retryCount > 5) {
      if (isMounted.current) {
        setLoading(false);
        setRefreshing(false);
        showToast('Erreur : trop de tentatives. Veuillez réessayer plus tard.', 'danger');
      }
      return;
    }

    // Définir l'état de chargement
    if (forceRefresh) setRefreshing(true);
    else setLoading(true);
    setDataSource('');

    const fetchPromise = (async () => {
      try {
        // 1. Cache si non forcé
        if (!forceRefresh) {
          const cached = await getCachedEmployes(cacheKey);
          if (cached) {
            if (isMounted.current) {
              setEmployees(cached.data);
              setPagination(cached.pagination);
              setDataSource('cache');
              setLoading(false);
              setRefreshing(false);
            }
            return cached;
          }
        }

        // 2. Requête réseau
        const url = `${API_URL}?action=fetch&page=${page}&limit=${pagination.limit}&search=${encodeURIComponent(search)}`;
        const response = await fetch(url, { signal: controller.signal });

        // 3. Gestion 429
        if (response.status === 429) {
          const stale = await getStaleEmployes(cacheKey);
          if (stale) {
            if (isMounted.current) {
              setEmployees(stale.data);
              setPagination(stale.pagination);
              setDataSource('cache (périmé)');
              showToast('429 – Utilisation du cache (données peut-être obsolètes)', 'warning');
              setLoading(false);
              setRefreshing(false);
            }
            return stale;
          }
          // Sinon, backoff exponentiel
          const waitTime = Math.min(1000 * Math.pow(2, retryCount), 30000);
          showToast(`Trop de requêtes. Nouvel essai dans ${waitTime/1000}s`, 'warning');
          await new Promise(resolve => setTimeout(resolve, waitTime));
          return fetchEmployees(page, search, forceRefresh, retryCount + 1);
        }

        // 4. Gestion des autres erreurs HTTP
        if (!response.ok) {
          const stale = await getStaleEmployes(cacheKey);
          if (stale) {
            if (isMounted.current) {
              setEmployees(stale.data);
              setPagination(stale.pagination);
              setDataSource('cache (périmé)');
              showToast('Erreur réseau – Utilisation du cache', 'warning');
              setLoading(false);
              setRefreshing(false);
            }
            return stale;
          }
          throw new Error(`HTTP ${response.status}`);
        }

        // 5. Lecture JSON
        const data = await response.json();
        if (data.status === 'success') {
          const payload = {
            data: data.data,
            pagination: data.pagination
          };
          if (isMounted.current) {
            setEmployees(data.data);
            setPagination(data.pagination);
            const source = forceRefresh ? 'live (forcé)' : 'live';
            setDataSource(source);
            await setCachedEmployes(cacheKey, payload);
            if (forceRefresh) {
              showToast('Données actualisées', 'success');
            }
            setLoading(false);
            setRefreshing(false);
          }
          return payload;
        } else {
          throw new Error(data.message || 'Erreur serveur');
        }
      } catch (err) {
        if (err.name === 'AbortError') return null;
        // Dernier recours : cache périmé
        const stale = await getStaleEmployes(cacheKey);
        if (stale && isMounted.current) {
          setEmployees(stale.data);
          setPagination(stale.pagination);
          setDataSource('cache (périmé)');
          showToast('Erreur – Utilisation du cache', 'warning');
          setLoading(false);
          setRefreshing(false);
          return stale;
        }
        if (isMounted.current) {
          showToast(err.message || 'Erreur de chargement', 'danger');
          setLoading(false);
          setRefreshing(false);
        }
        throw err;
      }
    })();

    // Stocker la promesse dans la Map pour dédoublonner
    pendingFetches.set(requestKey, fetchPromise);
    try {
      const result = await fetchPromise;
      return result;
    } finally {
      pendingFetches.delete(requestKey);
      if (abortControllerRef.current === controller) {
        abortControllerRef.current = null;
      }
    }
  }, [pagination.limit, showToast]);

  // ============================================================
  // 6. EFFETS : INITIALISATION ET NETTOYAGE
  // ============================================================
  useEffect(() => {
    isMounted.current = true;
    fetchEmployees(1, '', false);
    return () => {
      isMounted.current = false;
      if (abortControllerRef.current) abortControllerRef.current.abort();
      if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
      if (toastTimeoutRef.current) clearTimeout(toastTimeoutRef.current);
      pendingFetches.clear();
    };
  }, []);

  // ============================================================
  // 7. RECHERCHE (debounce)
  // ============================================================
  const handleSearch = (e) => {
    const val = e.target.value;
    setSearchTerm(val);
    if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
    searchTimeoutRef.current = setTimeout(() => {
      fetchEmployees(1, val, false);
    }, 400);
  };

  const handleRefresh = () => {
    if (!refreshing && !loading) {
      fetchEmployees(pagination.currentPage, searchTerm, true);
    }
  };

  const handleRefreshForce = () => {
    if (!refreshing && !loading) {
      // Vider le cache avant de forcer le rechargement
      db.employes.clear().then(() => {
        fetchEmployees(pagination.currentPage, searchTerm, true);
      });
    }
  };

  // ============================================================
  // 8. EXPORT CSV
  // ============================================================
  const exportCSV = () => {
    if (!employees.length) {
      showToast('Aucun employé à exporter.', 'warning');
      return;
    }
    const headers = ['Matricule', 'Nom', 'Prénom', 'Email', 'Téléphone', 'Poste', 'Service', 'Salaire', 'Statut'];
    const rows = employees.map(e => [
      e.matricule,
      e.nom,
      e.prenom,
      e.email,
      e.telephone,
      e.poste,
      e.service,
      e.salaireDeBase,
      e.statut
    ]);
    const csvContent = [headers, ...rows].map(row => row.join(',')).join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `employes_${new Date().toISOString().slice(0,10)}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  // ============================================================
  // 9. OUVRIR LA MODALE DE PAIEMENT
  // ============================================================
  const openPaymentModal = (emp, fiche = null) => {
    if (fiche) {
      setEditingPayroll(true);
      setFormData({
        id: fiche.id,
        userId: userId,
        employeId: fiche.employeId,
        nom: emp.nom,
        prenom: emp.prenom,
        periode: fiche.periode,
        salaireBase: parseFloat(fiche.salaireBase) || 0,
        primes: parseFloat(fiche.primes) || 0,
        cotisations: parseFloat(fiche.cotisations) || 0,
        netAPayer: parseFloat(fiche.netAPayer) || 0,
        statut: fiche.statut || 'Payé'
      });
    } else {
      setEditingPayroll(false);
      setFormData({
        id: '',
        userId: userId,
        employeId: emp.id,
        nom: emp.nom,
        prenom: emp.prenom,
        periode: '',
        salaireBase: parseFloat(emp.salaireDeBase) || 0,
        primes: 0,
        cotisations: 0,
        netAPayer: 0,
        statut: 'Payé'
      });
    }
    setShowPaymentModal(true);
  };

  // ============================================================
  // 10. SOUMISSION DU PAIEMENT (avec fetchJSON)
  // ============================================================
  const handlePaymentSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    const method = editingPayroll ? 'PUT' : 'POST';
    const action = editingPayroll ? 'update' : 'create';
    const payload = {
      ...formData,
      userId: userId
    };
    if (!editingPayroll) {
      delete payload.id;
    } else {
      payload.id = formData.id;
    }

    try {
      const data = await fetchJSON(`${API_URL}?action=${action}`, {
        method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });
      if (data.status === 'success') {
        showToast(data.message, 'success');
        setShowPaymentModal(false);
        // Invalider le cache après modification
        await db.employes.clear();
        fetchEmployees(pagination.currentPage, searchTerm, true);
      } else {
        showToast(data.message || 'Erreur', 'danger');
      }
    } catch (err) {
      showToast(err.message || 'Erreur serveur', 'danger');
    } finally {
      setSubmitting(false);
    }
  };

  const handleNumberChange = (e) => {
    const { name, value } = e.target;
    const parsed = value === '' ? 0 : parseFloat(value);
    setFormData(prev => ({ ...prev, [name]: isNaN(parsed) ? 0 : parsed }));
  };

  // ============================================================
  // 11. OUVERTURE DE LA MODALE DE SUPPRESSION
  // ============================================================
  const openDeleteModal = (ficheId) => {
    setDeleteId(ficheId);
    setShowDeleteModal(true);
  };

  const handleDelete = async () => {
    setDeleting(true);
    try {
      const data = await fetchJSON(`${API_URL}?action=delete&id=${deleteId}&userId=${userId}`, {
        method: 'DELETE'
      });
      if (data.status === 'success') {
        showToast(data.message, 'success');
        setShowDeleteModal(false);
        await db.employes.clear();
        fetchEmployees(pagination.currentPage, searchTerm, true);
      } else {
        showToast(data.message || 'Erreur', 'danger');
      }
    } catch (err) {
      showToast(err.message || 'Erreur serveur', 'danger');
    } finally {
      setDeleting(false);
    }
  };

  // ============================================================
  // 12. AFFICHAGE DU CHARGEMENT
  // ============================================================
  if (loading && !employees.length && !refreshing) {
    return (
      <div className="text-center py-5">
        <Spinner animation="border" variant="primary" />
        <p className="text-secondary mt-2">Chargement des employés...</p>
      </div>
    );
  }

  // ============================================================
  // 13. RENDU PRINCIPAL (JSX)
  // ============================================================
  return (
    <>
      <style>{`
        /* ============================================================
           DARK BLUE GLASSMORPHISM – MANAGE EMPLOYES MODAL
           ============================================================ */
        :root {
          --accent-blue: #3b82f6;
          --accent-dark-blue: #1d4ed8;
          --accent-blue-dim: rgba(59, 130, 246, 0.10);
          --accent-blue-glow: rgba(59, 130, 246, 0.25);
          --bg-primary: #0b1428;
          --bg-gradient: radial-gradient(circle at 20% 30%, #132044, #0a0f1f);
          --card-bg: rgba(20, 40, 80, 0.55);
          --card-border: rgba(59, 130, 246, 0.15);
          --text-primary: #e0e7ff;
          --text-secondary: #94a3b8;
          --text-muted: #64748b;
          --radius: 16px;
          --shadow: 0 12px 40px rgba(0, 0, 0, 0.5);
        }

        .monetra-page {
          background: var(--bg-gradient);
          min-height: 100vh;
          padding: 30px;
          color: var(--text-primary);
          font-family: 'Inter', sans-serif;
        }

        .glass-card {
          background: var(--card-bg);
          backdrop-filter: blur(16px);
          -webkit-backdrop-filter: blur(16px);
          border: 1px solid var(--card-border);
          border-radius: var(--radius);
          padding: 1.8rem;
          box-shadow: var(--shadow);
          transition: all 0.2s ease;
        }
        .glass-card:hover {
          border-color: rgba(59, 130, 246, 0.25);
          box-shadow: 0 12px 40px rgba(59, 130, 246, 0.05);
        }

        .badge-source {
          font-size: 11px;
          padding: 4px 12px;
          border-radius: 40px;
          background: rgba(59,130,246,0.15);
          color: var(--accent-blue);
          border: 1px solid rgba(59,130,246,0.12);
          white-space: nowrap;
        }
        .badge-source.live {
          background: rgba(46,204,113,0.15);
          color: #2ecc71;
          border-color: rgba(46,204,113,0.12);
        }
        .badge-source.warning {
          background: rgba(245,158,11,0.15);
          color: #f59e0b;
          border-color: rgba(245,158,11,0.12);
        }

        .monetra-btn {
          padding: 10px 24px;
          border-radius: 30px;
          border: 1px solid rgba(255,255,255,0.06);
          background: rgba(255,255,255,0.04);
          color: var(--text-primary);
          transition: all 0.25s ease;
          display: inline-flex;
          align-items: center;
          gap: 8px;
          font-weight: 500;
          cursor: pointer;
          font-size: 0.9rem;
          white-space: nowrap;
        }
        .monetra-btn:hover:not(:disabled) {
          background: rgba(255,255,255,0.08);
          transform: translateY(-2px);
          box-shadow: 0 6px 16px rgba(0,0,0,0.2);
        }
        .monetra-btn:disabled {
          opacity: 0.4;
          pointer-events: none;
        }
        .monetra-btn.primary {
          background: var(--accent-blue);
          color: #fff;
          border: none;
          box-shadow: 0 6px 20px var(--accent-blue-glow);
        }
        .monetra-btn.primary:hover:not(:disabled) {
          background: var(--accent-dark-blue);
          box-shadow: 0 8px 28px rgba(59, 130, 246, 0.4);
        }
        .monetra-btn.danger {
          border-color: #ef4444;
          color: #f87171;
        }
        .monetra-btn.danger:hover:not(:disabled) {
          background: rgba(239, 68, 68, 0.12);
        }
        .monetra-btn.outline {
          border-color: rgba(255,255,255,0.08);
          color: var(--text-secondary);
        }
        .monetra-btn.outline:hover {
          background: rgba(255,255,255,0.05);
        }

        .search-wrapper {
          position: relative;
          display: flex;
          align-items: center;
          background: rgba(255,255,255,0.03);
          border: 1px solid rgba(255,255,255,0.06);
          border-radius: 30px;
          padding: 4px 4px 4px 20px;
          transition: all 0.3s ease;
        }
        .search-wrapper:focus-within {
          border-color: var(--accent-blue);
          box-shadow: 0 0 20px rgba(59, 130, 246, 0.08);
        }
        .search-wrapper input {
          background: transparent;
          border: none;
          color: var(--text-primary);
          outline: none;
          padding: 10px 0;
          flex: 1;
        }
        .search-wrapper input::placeholder {
          color: var(--text-muted);
        }

        .glass-table {
          width: 100%;
          border-collapse: collapse;
        }
        .glass-table th {
          padding: 15px 12px;
          text-align: left;
          font-size: 0.75rem;
          text-transform: uppercase;
          letter-spacing: 1px;
          color: var(--accent-blue);
          border-bottom: 1px solid rgba(255,255,255,0.04);
        }
        .glass-table td {
          padding: 14px 12px;
          border-bottom: 1px solid rgba(255,255,255,0.02);
        }
        .glass-table tr {
          transition: 0.2s;
        }
        .glass-table tr:hover {
          background: rgba(255,255,255,0.03);
        }

        .status-badge {
          padding: 4px 12px;
          border-radius: 20px;
          font-size: 0.7rem;
          font-weight: 600;
        }
        .status-badge.Actif {
          background: rgba(59, 130, 246, 0.15);
          color: var(--accent-blue);
        }
        .status-badge.Congé {
          background: rgba(251, 191, 36, 0.15);
          color: #fbbf24;
        }
        .status-badge.Démissionné {
          background: rgba(239, 68, 68, 0.15);
          color: #ef4444;
        }

        .monetra-modal .modal-content {
          background: rgba(10, 20, 45, 0.92);
          backdrop-filter: blur(30px);
          -webkit-backdrop-filter: blur(30px);
          border: 1px solid rgba(59, 130, 246, 0.12);
          border-radius: 28px;
          color: var(--text-primary);
          box-shadow: 0 30px 80px rgba(0,0,0,0.8);
        }
        .monetra-modal .modal-header {
          border-bottom: 1px solid rgba(59, 130, 246, 0.06);
        }
        .monetra-modal .modal-footer {
          border-top: 1px solid rgba(59, 130, 246, 0.06);
        }
        .monetra-modal .btn-close {
          filter: invert(1) brightness(200%);
          opacity: 0.5;
        }

        .monetra-input {
          background: rgba(255,255,255,0.04);
          border: 1px solid rgba(255,255,255,0.06);
          border-radius: 14px;
          color: var(--text-primary);
          padding: 12px;
          width: 100%;
          transition: 0.3s;
        }
        .monetra-input:focus {
          border-color: var(--accent-blue);
          outline: none;
          background: rgba(255,255,255,0.06);
          box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.06);
        }
        .monetra-input::placeholder {
          color: var(--text-muted);
        }
        .monetra-input option {
          background: #0b1428;
          color: var(--text-primary);
        }

        .toast-monetra {
          background: rgba(10, 20, 45, 0.8) !important;
          backdrop-filter: blur(24px) !important;
          -webkit-backdrop-filter: blur(24px) !important;
          border: 1px solid rgba(59, 130, 246, 0.2) !important;
          border-radius: 16px !important;
          color: var(--text-primary) !important;
          box-shadow: 0 20px 50px rgba(0,0,0,0.6) !important;
        }

        .text-secondary { color: var(--text-secondary); }
        .text-muted { color: var(--text-muted); }
        .text-success { color: var(--accent-blue); }
        .text-danger { color: #ef4444; }
        .fw-bold { font-weight: 600; }
        .small { font-size: 0.85rem; }
        .m-0 { margin: 0; }
        .mb-4 { margin-bottom: 1.5rem; }
        .me-2 { margin-right: 0.5rem; }
        .p-2 { padding: 0.5rem; }
        .p-3 { padding: 1rem; }
        .py-5 { padding-top: 3rem; padding-bottom: 3rem; }
        .gap-2 { gap: 0.5rem; }
        .gap-3 { gap: 1rem; }
        .d-flex { display: flex; }
        .flex-wrap { flex-wrap: wrap; }
        .align-items-center { align-items: center; }
        .justify-content-between { justify-content: space-between; }
        .text-end { text-align: right; }
        .text-center { text-align: center; }
        .overflow-hidden { overflow: hidden; }
        .table-responsive { overflow-x: auto; }
        .border-top { border-top: 1px solid rgba(255,255,255,0.04); }
        .border-opacity-10 { opacity: 0.1; }
        .ms-auto { margin-left: auto; }

        .btn-close-custom {
          background: transparent;
          border: none;
          color: var(--text-secondary);
          opacity: 0.5;
          cursor: pointer;
          font-size: 1.2rem;
          padding: 0 4px;
          margin-left: auto;
          transition: color 0.2s;
        }
        .btn-close-custom:hover {
          opacity: 1;
        }

        @media (max-width: 768px) {
          .monetra-page { padding: 16px; }
          .glass-card { padding: 1.2rem; }
          .glass-table th, .glass-table td { font-size: 0.7rem; padding: 8px 6px; }
        }
      `}</style>

      <div className="monetra-page">
        {/* Header avec badge source et boutons */}
        <div className="d-flex justify-content-between align-items-center mb-4 flex-wrap gap-3">
          <div>
            <h1 className="display-6 fw-bold mb-1" style={{ color: 'var(--text-primary)' }}>
              Gestion des paiements
            </h1>
            <p className="text-secondary m-0">Consultez les employés et gérez leurs fiches de paie.</p>
          </div>
          <div className="d-flex align-items-center gap-2 flex-wrap">
            <span className={`badge-source ${dataSource === 'live' || dataSource === 'live (forcé)' ? 'live' : dataSource === 'cache (périmé)' ? 'warning' : ''}`}>
              {dataSource === 'cache' ? '📦 Cache' :
               dataSource === 'cache (périmé)' ? '⚠️ Cache périmé' :
               dataSource === 'cache (partagé)' ? '🔄 Partage' :
               dataSource === 'live' ? '🔄 Live' :
               dataSource === 'live (forcé)' ? '🔄 Forcé' : '📦 Cache'}
            </span>
            <button
              className="monetra-btn outline"
              onClick={handleRefresh}
              disabled={refreshing || loading}
            >
              <FontAwesomeIcon icon={faSyncAlt} spin={refreshing} /> Actualiser
            </button>
            <button
              className="monetra-btn outline"
              onClick={handleRefreshForce}
              disabled={refreshing || loading}
            >
              <FontAwesomeIcon icon={faSyncAlt} spin={refreshing} style={{ color: '#ef4444' }} /> Forcer
            </button>
            <button className="monetra-btn" onClick={exportCSV}>
              <FontAwesomeIcon icon={faFileCsv} /> Exporter CSV
            </button>
          </div>
        </div>

        {/* Recherche */}
        <div className="glass-card mb-4 d-flex justify-content-between align-items-center flex-wrap gap-3">
          <div className="search-wrapper" style={{ minWidth: '260px', flex: 1, maxWidth: '420px' }}>
            <FontAwesomeIcon icon={faSearch} className="text-secondary" />
            <input
              type="text"
              placeholder="Rechercher (nom, email, matricule...)"
              value={searchTerm}
              onChange={handleSearch}
            />
          </div>
          <div className="text-secondary small">
            <span>{pagination.totalRecords} enregistrements</span>
          </div>
        </div>

        {/* Tableau */}
        <div className="glass-card p-0 overflow-hidden">
          <div className="table-responsive">
            <table className="glass-table">
              <thead>
                <tr>
                  <th>Matricule</th>
                  <th>Nom & Prénom</th>
                  <th>Contact</th>
                  <th>Poste / Service</th>
                  <th>Statut</th>
                  <th className="text-end">Actions</th>
                </tr>
              </thead>
              <tbody>
                {loading && !employees.length && !refreshing ? (
                  <tr><td colSpan="6" className="text-center py-5"><div className="spinner-border text-primary" role="status"></div></td></tr>
                ) : employees.length === 0 ? (
                  <tr><td colSpan="6" className="text-center py-5 text-secondary">Aucun employé trouvé.</td></tr>
                ) : (
                  employees.map(emp => (
                    <tr key={emp.id}>
                      <td><span className="fw-bold text-secondary">{emp.matricule}</span></td>
                      <td><strong>{emp.prenom} {emp.nom}</strong></td>
                      <td>
                        <div className="small text-secondary">{emp.email}</div>
                        <div className="small text-secondary">{emp.telephone}</div>
                      </td>
                      <td>
                        <div className="fw-medium">{emp.poste}</div>
                        <div className="small text-secondary">{emp.service}</div>
                      </td>
                      <td><span className={`status-badge ${emp.statut}`}>{emp.statut}</span></td>
                      <td className="text-end">
                        <Link to={`/profil-employe/${emp.id}`} className="monetra-btn me-2 p-2">
                          <FontAwesomeIcon icon={faUser} />
                        </Link>
                        <button className="monetra-btn me-2 p-2" onClick={() => openPaymentModal(emp)}>
                          <FontAwesomeIcon icon={faHandHoldingDollar} />
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          <div className="p-3 border-top border-secondary border-opacity-10 d-flex justify-content-between align-items-center flex-wrap">
            <span className="text-secondary small">Page {pagination.currentPage} sur {pagination.totalPages}</span>
            <div className="d-flex gap-2">
              <button
                className="monetra-btn p-2"
                disabled={pagination.currentPage <= 1 || loading || refreshing}
                onClick={() => fetchEmployees(pagination.currentPage - 1, searchTerm)}
              >
                ‹
              </button>
              <button
                className="monetra-btn p-2"
                disabled={pagination.currentPage >= pagination.totalPages || loading || refreshing}
                onClick={() => fetchEmployees(pagination.currentPage + 1, searchTerm)}
              >
                ›
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* --- MODALE CRÉATION/ÉDITION DE FICHE DE PAIE --- */}
      <Modal show={showPaymentModal} onHide={() => setShowPaymentModal(false)} centered className="monetra-modal">
        <Form onSubmit={handlePaymentSubmit}>
          <Modal.Header closeButton>
            <Modal.Title>
              <FontAwesomeIcon icon={editingPayroll ? faEdit : faPlus} className="me-2" style={{ color: 'var(--accent-blue)' }} />
              {editingPayroll ? 'Modifier' : 'Ajouter'} une fiche de paie pour {formData.nom} {formData.prenom}
            </Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <div className="row g-3">
              <div className="col-md-6">
                <label className="text-secondary small">ID Employé</label>
                <input className="monetra-input" name="employeId" value={formData.employeId} readOnly />
              </div>
              <div className="col-md-6">
                <label className="text-secondary small">Période (ex: 06/2026)</label>
                <input className="monetra-input" name="periode" value={formData.periode} onChange={(e) => setFormData({...formData, periode: e.target.value})} required disabled={submitting} />
              </div>
              <div className="col-md-6">
                <label className="text-secondary small">Salaire de base ($)</label>
                <input type="number" step="0.01" className="monetra-input" name="salaireBase" value={formData.salaireBase} onChange={handleNumberChange} required disabled={submitting} />
              </div>
              <div className="col-md-6">
                <label className="text-secondary small">Primes ($)</label>
                <input type="number" step="0.01" className="monetra-input" name="primes" value={formData.primes} onChange={handleNumberChange} required disabled={submitting} />
              </div>
              <div className="col-md-6">
                <label className="text-secondary small">Cotisations ($)</label>
                <input type="number" step="0.01" className="monetra-input" name="cotisations" value={formData.cotisations} onChange={handleNumberChange} required disabled={submitting} />
              </div>
              <div className="col-md-6">
                <label className="text-secondary small">Net à payer ($)</label>
                <input type="number" step="0.01" className="monetra-input" name="netAPayer" value={formData.netAPayer} onChange={handleNumberChange} required disabled={submitting} />
              </div>
              <div className="col-md-6">
                <label className="text-secondary small">Statut</label>
                <select className="monetra-input" name="statut" value={formData.statut} onChange={(e) => setFormData({...formData, statut: e.target.value})} disabled={submitting}>
                  <option value="Payé">Payé</option>
                  <option value="En attente">En attente</option>
                  <option value="Impayé">Impayé</option>
                </select>
              </div>
            </div>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="outline-secondary" onClick={() => setShowPaymentModal(false)} disabled={submitting}>Annuler</Button>
            <Button variant="success" type="submit" className="monetra-btn primary" disabled={submitting}>
              {submitting ? (
                <><FontAwesomeIcon icon={faSpinner} spin /> Enregistrement...</>
              ) : (
                'Enregistrer'
              )}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>

      {/* --- MODALE SUPPRESSION --- */}
      <Modal show={showDeleteModal} onHide={() => setShowDeleteModal(false)} centered className="monetra-modal">
        <Modal.Header closeButton>
          <Modal.Title className="text-danger">Confirmation de suppression</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          Êtes-vous sûr de vouloir supprimer cette fiche de paie ?
          <br /><span className="text-secondary small">Cette action est irréversible et ajustera la caisse.</span>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="outline-secondary" onClick={() => setShowDeleteModal(false)} disabled={deleting}>Annuler</Button>
          <Button variant="danger" onClick={handleDelete} className="monetra-btn danger" disabled={deleting}>
            {deleting ? (
              <><FontAwesomeIcon icon={faSpinner} spin /> Suppression...</>
            ) : (
              'Supprimer'
            )}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* --- TOAST --- */}
      {toast.show && (
        <div style={{ position: 'fixed', top: '20px', right: '20px', zIndex: 9999 }}>
          <div className={`toast-monetra p-3 ${toast.variant}`} style={{ minWidth: '300px' }}>
            <div className="d-flex align-items-center gap-3">
              <FontAwesomeIcon
                icon={toast.variant === 'success' ? faCheckCircle : faExclamationTriangle}
                className={toast.variant === 'success' ? 'text-success' : 'text-danger'}
              />
              <span>{toast.message}</span>
              <button
                className="btn-close-custom ms-auto"
                onClick={() => setToast({ ...toast, show: false })}
                style={{ background: 'transparent', border: 'none', color: 'var(--text-primary)', opacity: 0.5, fontSize: '1.2rem' }}
              >
                ✕
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default ManageEmployesModal;